import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api } from '@/libs/api'
import i18n from '@/libs/i18n'
import { moduleUnlicensed } from '@/libs/license'
import { answerVerdict } from '@/libs/operation'
import { withOrderPin } from '@/libs/orderPin'
import { useAppStore } from '@/stores/app'
import type {
    IGuestAssistantActions,
    IGuestChatMessage,
    IGuestChatOperation,
    IGuestChatReply,
    IGuestChatThread,
    IGuestChatTurnError,
    IGuestChatTurnStarted,
    IGuestChatTurnView,
} from '@/types/Chat'

/**
 * The guest's conversation with Milo.
 *
 * # Why the thread is the SERVICE's and not this store's
 *
 * Three reasons, and each of them is enough on its own (plan §2.2). The model must never
 * be handed a history the browser composed — the browser here is the guest's. The daily
 * budget has to be counted somewhere the browser cannot reset. And a conversation that
 * lived only in this telephone could not be deleted by the hotel, which is the party
 * that owes the guest an erasure. So this store holds a COPY of what the service says,
 * asks for it once when the sheet is first opened, and never invents a line.
 *
 * # Why the sheet's open state lives here
 *
 * The button and the sheet are two components mounted side by side on the shell, and
 * neither owns the other. A prop passed down from `App.vue` would make the shell hold a
 * piece of the assistant's state for no other reason than that it is above both.
 */

/**
 * The API's codes for what Milo can refuse (plan §5, `pkg/enums/error`).
 *
 * They pick SENTENCES and nothing else. Whether a press may be made again and whether the
 * thread has to be read afresh come from the refusal's own `retryable` /
 * `operation_closed`, through `libs/operation.ts` — no screen and no store in this
 * product decides that from a code (root `CLAUDE.md`, deviation 42).
 */
const GUEST_ASSISTANT_OFF           = 52
const GUEST_ASSISTANT_BUDGET_SPENT  = 53
const GUEST_ASSISTANT_THROTTLED     = 54
const GUEST_OPERATION_EXPIRED       = 55

/** The cards that order or book, and so ask for the house's PIN (Logic.ConfirmGuestChatOperation). */
const PIN_CARDS = ['guest_service_booking', 'guest_room_service_order']
const GUEST_OPERATION_DECIDED       = 56

/**
 * The two ways a turn ends with nobody having refused anything (docs/AI.md §31): Milo
 * used his whole budget for the question, or the process working it out went away
 * mid-answer — a deploy.
 *
 * Their own codes because the sheet says a different thing about each: „vprašaj ožje“ and
 * „vprašaj znova“. Neither is a refusal — the house has not switched anything off and the
 * guest is out of nothing — so neither may be shown with the sentence that says so.
 */
const ASSISTANT_TURN_TIMED_OUT      = 95
const ASSISTANT_TURN_LOST           = 96

/**
 * How long a wait may go unexplained, in milliseconds.
 *
 * Five seconds, the same number the reception portal uses. Under it the plain "Milo
 * razmišlja …" is the honest thing to show; past it, the turn is one that went and read
 * something, and a line that has not changed in forty seconds on a telephone reads as a
 * page that has hung — which is when somebody reloads and throws away a turn that was
 * about to arrive AND has already been paid for.
 */
const STILL_WORKING_AFTER = 5000

/**
 * How often the sheet asks again for an answer that is still being written, and for how
 * long, in milliseconds.
 *
 * Two seconds is what the API's own Retry-After says. Five minutes is the SERVER's own
 * budget for a turn: asking beyond it would be waiting on something that has been
 * abandoned, and giving up before it would throw away an answer the house has already
 * paid for — which is exactly what a reload used to do.
 */
const TURN_POLL_EVERY  = 2000
const TURN_POLL_BUDGET = 5 * 60 * 1000

/** The house has switched every action off — the shape used before the stay is read. */
const NO_ACTIONS: IGuestAssistantActions = {
    late_checkout: false,
    services:      false,
    room_requests: false,
    messages:      false,
}

const errorCode = (body: unknown): number =>
    (body as { error?: { code?: number } })?.error?.code ?? 0

export const useChatStore = defineStore('chat', () => {
    const appStore = useAppStore()

    const open     = ref(false)
    const messages = ref<IGuestChatMessage[]>([])

    const loaded  = ref(false)
    const loading = ref(false)
    const sending = ref(false)
    /** Whether the wait has gone on long enough to be worth a second line. */
    const stillWorking = ref(false)

    /** An answer has arrived while the sheet was shut — the dot on the button. */
    const unread = ref(false)

    /** Which card is being decided, so only that one shows a busy button. */
    const deciding = ref('')

    /**
     * What one card has to say about its own last refusal, as an i18n key, by operation
     * id.
     *
     * Beside the thread rather than on the card, for two reasons. The card is the
     * SERVICE's record and `result` on it is the service's own audit line — writing a
     * portal key into that field would be this portal editing the hotel's books. And the
     * thread is replaced wholesale when it is re-read, which is exactly what happens
     * after these two refusals; a note kept on the object would be thrown away with it.
     */
    const cardNotes = ref<Record<string, string>>({})

    /**
     * What the sheet has to say about its own last refusal, as an i18n key.
     *
     * A key and not a sentence: the guest may change language with the sheet open, and a
     * sentence frozen at the moment of the refusal would then be the one line on screen
     * still speaking the old one.
     */
    const notice = ref('')

    /**
     * The house switched Milo off while this page was open (code 52).
     *
     * The stay read is what normally decides whether there is a button, so a guest
     * practically never meets this — it is the race between a hotelier's switch and a
     * telephone left on a table. The button then goes away rather than staying to be
     * pressed into the same refusal.
     */
    const revoked = ref(false)

    const settings = computed(() => appStore.stay?.assistant ?? null)

    /** What the house allows Milo to prepare. Everything off until the stay is read. */
    const actions = computed((): IGuestAssistantActions => settings.value?.actions ?? NO_ACTIONS)

    /**
     * Whether there is an assistant on this screen at all.
     *
     * The stay read is the gate: the API answers `assistant.enabled`, and a portal that
     * asked a second endpoint would be a spinner in front of a guest for one boolean. A
     * dead link and an unlicensed module both take the button away too — the shell draws
     * a refusal in place of the whole portal, and a floating button over it would be the
     * one thing on that screen still offering to help.
     */
    const available = computed((): boolean =>
        !!settings.value?.enabled
        && !revoked.value
        && !appStore.failed
        && !moduleUnlicensed.value)

    /** A proposal nobody has decided — what the dot on the button also means. */
    const proposalWaiting = computed((): boolean => messages.value.some(message =>
        (message.operations ?? []).some(operation => operation.status === 'proposed')))

    /** Reads one refusal, and returns whether the sheet should stop offering to talk. */
    const noteRefusal = (body: unknown): void => {
        switch (errorCode(body)) {
            case GUEST_ASSISTANT_OFF:
                revoked.value = true
                notice.value  = 'milo.unavailable'
                break

            // Two different sentences on purpose: one is "come back tomorrow, or ring
            // the desk", the other is "wait a moment and ask again". A guest who is out
            // of nothing must not be told they are.
            case GUEST_ASSISTANT_BUDGET_SPENT:
                notice.value = 'milo.budget_spent'
                break

            case GUEST_ASSISTANT_THROTTLED:
                notice.value = 'milo.throttled'
                break

            // Neither of these is a refusal, and the sheet must not say the guest is out
            // of anything: one asks for a narrower question, the other simply for the
            // same one again.
            case ASSISTANT_TURN_TIMED_OUT:
                notice.value = 'milo.too_long'
                break

            case ASSISTANT_TURN_LOST:
                notice.value = 'milo.interrupted'
                break

            default:
                notice.value = 'milo.failed'
        }
    }

    /**
     * Reads the stored thread. Once, when the sheet is first opened.
     *
     * Not on mount: a guest who never opens Milo never pays for the request, and the
     * button is drawn from the stay, which is already loaded.
     */
    const load = async (): Promise<void> => {
        if (loading.value) return

        loading.value = true

        const { status, data } = await api<IGuestChatThread>('/chat')

        loading.value = false

        if (status === 200 && data) {
            messages.value = data.messages ?? []
            loaded.value   = true
            notice.value   = ''

            // AN ANSWER STILL ON ITS WAY. On a telephone this is the ordinary case and
            // not the exception: a guest asks, the screen locks, and the sheet is read
            // again from the start when they come back. The thread says which turn it is
            // waiting for, so the sheet goes back to waiting instead of showing a
            // question with nothing under it.
            if (data.running_turn_id) void resumeTurn(data.running_turn_id)

            return
        }

        // A thread that has never been spoken to is not an error: the service answers an
        // empty one, and a 404 from an older API reads the same way. Anything else is a
        // refusal worth a line.
        if (status === 404) {
            messages.value = []
            loaded.value   = true

            return
        }

        noteRefusal(data)
    }

    /** Opens the sheet, and fetches the thread the first time it is opened. */
    const openSheet = async (): Promise<void> => {
        open.value   = true
        unread.value = false

        if (!loaded.value) await load()
    }

    const closeSheet = (): void => {
        open.value = false
    }

    /**
     * One turn.
     *
     * The guest's line goes into the thread before the request leaves, because on a
     * telephone the alternative is a tap that appears to have done nothing for five
     * seconds. It stays there if the turn is refused: the words are the guest's own and
     * deleting them would be this portal deciding they were not said.
     *
     * There is NO client-side timeout, deliberately. The turn is bounded on the server,
     * where the assistant's branch has its own five-minute budget and the tool loop a
     * smaller bound inside it — so a browser giving up first would abandon a turn that
     * was about to arrive, and the model call it abandoned would already have been paid
     * for out of the house's daily allowance.
     */
    const send = async (text: string): Promise<boolean> => {
        const question = text.trim()

        if (!question || sending.value || !available.value) return false

        notice.value = ''
        messages.value.push({ role: 'user', text: question })

        sending.value      = true
        stillWorking.value = false

        const hint = window.setTimeout(() => { stillWorking.value = true }, STILL_WORKING_AFTER)

        // The language the thread is held in is the portal's, reported per turn: the
        // guest may switch it mid-stay, and the service answers in the one they are
        // reading rather than the one their citizenship pointed at in the first minute.
        const { status, data } = await api<IGuestChatReply & Partial<IGuestChatTurnStarted>>('/chat', {
            method: 'POST',
            body:   JSON.stringify({ text: question, language: String(i18n.global.locale.value) }),
        })

        // THE TURN OUTLIVED ITS REQUEST, which is not a failure any more.
        //
        // The API waits forty-five seconds for the answer and then hands back the turn's
        // name instead, because the edge closes any request at ninety (docs/AI.md §32).
        // The answer is being written on the server and this asks for it until it has it.
        if (status === 202 && data?.turn_id) {
            window.clearTimeout(hint)

            return waitForTurn(data.turn_id)
        }

        window.clearTimeout(hint)
        stillWorking.value = false
        sending.value      = false

        if (status !== 200 || !data?.text) {
            noteRefusal(data)

            return false
        }

        appendAnswer({ text: data.text, operations: data.operations ?? [] })

        return true
    }

    /** Puts one of Milo's answers into the thread, and lights the dot if nobody is
     *  looking at it. Said once, because there are now three ways an answer arrives: the
     *  request that asked, a poll, and a sheet reopened onto a turn already running. */
    const appendAnswer = (reply: IGuestChatReply): void => {
        messages.value.push({
            role:       'assistant',
            text:       reply.text,
            operations: reply.operations ?? [],
        })

        if (!open.value) unread.value = true
    }

    /** What a FAILED turn says, as an i18n key — a key and not a sentence, for the reason
     *  `notice` is one: the guest may change language with the sheet open. */
    const noteTurnFailure = (error: IGuestChatTurnError | null): void => {
        if (error?.code === ASSISTANT_TURN_TIMED_OUT) {
            notice.value = 'milo.too_long'

            return
        }

        if (error?.code === ASSISTANT_TURN_LOST) {
            notice.value = 'milo.interrupted'

            return
        }

        notice.value = 'milo.failed'
    }

    /**
     * Waits for an answer that is being written on the server.
     *
     * # Why a lost connection is not a lost turn
     *
     * Because the answer is not in this telephone. The turn runs on a context detached
     * from any request, so a signal that comes and goes in a lift, a gateway answering
     * through a deploy, and a locked screen are all things to ask again through. Before
     * this, every one of them destroyed a turn the house had already been billed for out
     * of the booking's own daily allowance.
     */
    const waitForTurn = async (turnId: string): Promise<boolean> => {
        sending.value      = true
        // Straight to the second line: a turn that got this far has been going longer
        // than the five seconds that line waits for.
        stillWorking.value = true

        const until = Date.now() + TURN_POLL_BUDGET

        while (Date.now() < until) {
            await new Promise(resolve => { window.setTimeout(resolve, TURN_POLL_EVERY) })

            const { status, data } = await api<IGuestChatTurnView>(`/chat/turns/${turnId}`)

            // Still working, or the service is momentarily not there. Ask again.
            if (status === 0 || status === 202 || [502, 503, 504].includes(status)) continue

            sending.value      = false
            stillWorking.value = false

            if (status === 200 && data?.status === 'done' && data.reply) {
                appendAnswer(data.reply)

                return true
            }

            if (status === 200 && data?.status === 'failed') {
                noteTurnFailure(data.error ?? null)

                return false
            }

            // 404 and anything else: this turn is not answerable any more.
            notice.value = 'milo.interrupted'

            return false
        }

        sending.value      = false
        stillWorking.value = false
        notice.value       = 'milo.too_long'

        return false
    }

    /** Picks up an answer that was already being written when the sheet was opened. The
     *  guest's own line is in the thread already — it was stored with the turn — so
     *  nothing is written here but the waiting. */
    const resumeTurn = async (turnId: string): Promise<void> => {
        if (sending.value) return

        await waitForTurn(turnId)
    }

    /** Finds one card wherever it sits in the thread, so a decision lands on the object
     *  the sheet is actually drawing. */
    const cardById = (operationId: string): IGuestChatOperation | undefined =>
        messages.value.flatMap(message => message.operations ?? []).find(one => one.id === operationId)

    /**
     * Confirms or declines one proposal.
     *
     * What the press did is shown ON THE CARD and not only as a toast — a toast on a
     * telephone is gone by the time the thumb has travelled back to the card it came
     * from.
     *
     * **Whether the thread is re-read is the ANSWER's own verdict and not a code of
     * ours** (root `CLAUDE.md`, deviation 42). A refusal that closes the operation means
     * the card can achieve nothing more, and the thread is read again — because the
     * service resolves a card's status and outcome AT READ TIME, so the guest sees what
     * the first press actually did rather than a red line about a thing that worked. Two
     * codes were written out here to decide exactly that, and the service has answered it
     * itself since v1.566: a card left on a table for an hour (55) and a second press
     * after a lost answer (56) are both closed, and so is any other refusal that ends the
     * card — including ones this portal has never met.
     *
     * The two codes stay for what they are actually good for, which is the SENTENCE:
     * „potekla“ and „o njej je že bilo odločeno“ are different things to read.
     */
    const decide = async (operationId: string, confirm: boolean): Promise<boolean> => {
        const card = cardById(operationId)

        if (!card || deciding.value) return false

        deciding.value = operationId
        notice.value   = ''

        const verb = confirm ? 'confirm' : 'decline'
        const send = (pin: string) => api<IGuestChatOperation>(`/chat/operations/${operationId}/${verb}`, {
            method: 'POST',
            body:   JSON.stringify(confirm ? { pin } : {}),
        })

        // A card that orders or books is that act through another button, and asks for
        // the house's PIN exactly as the screens do (libs/orderPin.ts). The rest never do.
        const answer = confirm ? await withOrderPin(send, PIN_CARDS.includes(card.kind)) : await send('')

        deciding.value = ''

        if (!answer) return false

        const { status, data } = answer

        if (status === 200) {
            // A decline answers with nothing to say beyond the fact of it, so the card is
            // settled here; a confirm answers with the operation it executed.
            card.status  = confirm ? (data?.status ?? 'executed') : 'declined'
            card.result  = data?.result ?? ''
            card.outcome = data?.outcome ?? null

            return card.status === 'executed'
        }

        if (answerVerdict(status, data) === 'closed') {
            const code = errorCode(data)

            cardNotes.value[operationId] = code === GUEST_OPERATION_EXPIRED
                ? 'milo.expired'
                : code === GUEST_OPERATION_DECIDED ? 'milo.already_decided' : 'milo.card_closed'

            // The card as it IS now, from the one place that knows: a second press on a
            // card that was already confirmed has to show the FIRST press's outcome.
            await load()

            return false
        }

        noteRefusal(data)

        return false
    }

    const confirm = (operationId: string): Promise<boolean> => decide(operationId, true)
    const decline = (operationId: string): Promise<boolean> => decide(operationId, false)

    /** The i18n key one card's last refusal left behind, or an empty string. */
    const noteFor = (operationId: string): string => cardNotes.value[operationId] ?? ''

    return {
        open, messages, loading, sending, stillWorking, unread, deciding, notice,
        actions, available, proposalWaiting,
        load, openSheet, closeSheet, send, confirm, decline, noteFor,
    }
})
